import { sfx } from '../audio/sfx';
import { BrowserVoice, browserVoiceSupported } from '../voice/browserVoice';
import { LiveVoice, voiceStatus, type LiveState } from '../voice/live';
import type { Session } from './session';

type Status = { gptLive: boolean; reason?: string; passphrase?: boolean };

/**
 * Picks the best voice path and wires it to the game:
 * - GPT-Live (full duplex, tool calls through the Responses backend) when the
 *   server has an OpenAI key.
 * - Otherwise the browser's own speech recognition plus the local command
 *   grammar, which needs no key but only understands set phrases.
 */
export class VoiceController {
  private s: Session;
  private live: LiveVoice | null = null;
  private browser: BrowserVoice | null = null;
  private status: Status | null = null;
  mode: 'gpt-live' | 'browser' | null = null;
  active = false;

  constructor(s: Session) {
    this.s = s;
    voiceStatus()
      .then((st) => (this.status = st))
      .catch(() => (this.status = { gptLive: false, reason: 'Voice status unavailable' }));
  }

  get available(): Status | null {
    return this.status;
  }

  async toggle() {
    if (this.active) this.stop();
    else await this.start();
  }

  async start() {
    sfx.unlock();
    if (this.s.level.flags.unassisted) {
      this.s.hud.toast('This one is unassisted. No copilot: read it yourself.', 'warn');
      return;
    }
    const pref = this.s.settings.voiceMode;
    const st = this.status ?? (await voiceStatus().catch(() => ({ gptLive: false }) as Status));
    this.status = st;
    if (pref !== 'browser' && st.gptLive) return this.startLive();
    if (pref !== 'gpt-live' && browserVoiceSupported()) return this.startBrowser(st.gptLive ? undefined : st.reason);
    if (pref === 'gpt-live' && !st.gptLive) {
      this.s.hud.toast(`GPT-Live isn't set up: ${st.reason ?? 'no OpenAI key on the server'}. Switch voice to browser in settings, or add the key.`, 'warn');
      return;
    }
    this.s.hud.toast('Voice needs GPT-Live (an OpenAI key on the server) or a browser with speech recognition, like Chrome. The command bar (Enter) takes the same phrases.', 'warn');
  }

  private startLive() {
    const hud = this.s.hud;
    this.mode = 'gpt-live';
    this.active = true;
    hud.setVoice('connecting', 'Connecting');
    hud.captions('', '', 'Relay, on GPT-Live 1. Talk normally; you can interrupt.');
    this.live = new LiveVoice(
      {
        onState: (state: LiveState, detail?: string) => {
          if (state === 'live') {
            hud.setVoice('live', 'Relay live');
            sfx.play('voiceOn');
            this.live?.sendContext(this.briefing());
          } else if (state === 'connecting') hud.setVoice('connecting', 'Connecting');
          else if (state === 'error') {
            hud.setVoice('error', 'Voice error');
            hud.toast(`Voice: ${detail ?? 'connection failed'}`, 'bad');
            this.active = false;
          } else if (state === 'closed' || state === 'idle') {
            hud.setVoice('off', 'Voice');
            this.active = false;
          }
        },
        onUserTranscript: (text, final) => {
          hud.captions(text, null);
          if (final) this.s.world.log({ type: 'note', text: `You said: ${text}`, src: 'voice' });
        },
        onAssistantTranscript: (text) => {
          hud.captions(null, text);
        },
        onToolCall: async (name, args) => {
          const r = this.s.runTool(name, args, 'voice');
          if (name === 'get_state') return JSON.stringify(r.data);
          return JSON.stringify({ ok: r.ok, result: r.message, ...(r.data && typeof r.data === 'object' ? { data: r.data } : {}) });
        },
      },
      { passphrase: this.s.settings.voicePass || undefined },
    );
    this.live.start().catch((err: unknown) => {
      hud.setVoice('error', 'Voice error');
      hud.toast(`Voice: ${err instanceof Error ? err.message : String(err)}`, 'bad');
      this.active = false;
    });
  }

  private startBrowser(reason?: string) {
    const hud = this.s.hud;
    this.mode = 'browser';
    this.active = true;
    hud.setVoice('listening', 'Listening');
    hud.captions('', '', `Browser voice${reason ? ` (GPT-Live off: ${reason})` : ''}. Say things like "build two benches near forge 1".`);
    this.browser = new BrowserVoice({
      onPartial: (text) => hud.captions(text, null),
      onFinal: (text) => {
        hud.captions(text, null);
        const r = this.s.exec(text, 'voice');
        hud.captions(null, r.message);
        this.speak(r.message);
      },
      onState: (listening, error) => {
        if (error) {
          hud.setVoice('error', 'Voice error');
          hud.toast(`Voice: ${error}`, 'bad');
          this.active = false;
          return;
        }
        hud.setVoice(listening ? 'listening' : 'off', listening ? 'Listening' : 'Voice');
        if (!listening) this.active = false;
      },
    });
    this.browser.start();
    sfx.play('voiceOn');
  }

  private speak(text: string) {
    if (typeof speechSynthesis === 'undefined' || !text) return;
    const first = text.split(/(?<=\.)\s/)[0].slice(0, 120);
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(first);
    u.rate = 1.15;
    u.volume = 0.85;
    speechSynthesis.speak(u);
  }

  private briefing(): string {
    const w = this.s.world;
    const g = w.goalProgress();
    return `The player is on level ${w.level.index}, "${w.level.name}". Goal: ${g.label} (${g.value}/${g.target}). Budget ${Math.floor(w.credits)}, trust ${Math.round(w.trust)}. Call get_state before building so you know labels and positions.`;
  }

  /** Quiet context for the copilot. Only GPT-Live uses it. */
  context(text: string) {
    if (this.mode === 'gpt-live' && this.active) this.live?.sendContext(text);
  }

  stop() {
    // Read this first: the live client's own close callback clears it.
    const wasActive = this.active;
    if (this.live) {
      this.live.stop();
      this.live = null;
    }
    if (this.browser) {
      this.browser.stop();
      this.browser = null;
    }
    if (wasActive) sfx.play('voiceOff');
    this.active = false;
    this.mode = null;
    this.s.hud?.setVoice('off', 'Voice');
  }
}
